import React, { useState, useEffect } from "react";
import { Popover, Box, Tabs, Tab, IconButton } from "@mui/material";
import { CancelIcon } from "../../assets/icon";
import { useChatStore } from "../../stores/chatStore";
import { useAppStore } from "../../stores/appStore";

import { ReactionsPanel } from "./reactions-panel/reactions-panel";
import { CalendarPanel } from "./calendar-panel/calendar-panel";
import { LessonsPanel } from "./lessons-panel/lessons-panel";
import { CrashCoursesPanel } from "./crash-courses-panel/crash-courses-panel";
import { FavoriteMessagesPanel } from "./favorite-messages-panel/favorite-messages-panel";
import { SearchMessagesPanel } from "./search-messages-panel/search-messages-panel";
import { CustomTypography } from "../custom-typography/custom-typography";
import {
  popoverPaperStyles,
  headerBoxStyles,
  tabsStyles,
  contentBoxStyles,
} from "./styles";
import { TagMessagePanel } from "./tag-panel/tag-panel";

interface ChatInfoPanelProps {
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  initialTab?: string;
  onMessageSelect?: () => void;
}

export const ChatInfoPanel: React.FC<ChatInfoPanelProps> = ({
  isOpen,
  anchorEl,
  onClose,
  initialTab,
  onMessageSelect,
}) => {
  const tabs = [
    "Favorite",
    "My Tags",
    "Lessons",
    "Crash Courses",
    "Reactions",
    "Calendar",
    "Search",
  ];

  const mapInitialTab = (tab?: string) => {
    if (tab === "Lessons to answer") return "Lessons";
    return tab || "Favorite";
  };

  const initialLessonsViewMode =
    initialTab === "Lessons to answer" ? "unread" : undefined;

  const [isClosing, setIsClosing] = useState(false);
  const [contentSnapshot, setContentSnapshot] = useState<{
    isCrashCoursesToAnswer: boolean;
    activeTab: string;
  }>({
    isCrashCoursesToAnswer: initialTab === "Crash Courses to answer",
    activeTab: mapInitialTab(initialTab),
  });

  const isCrashCoursesToAnswer = initialTab === "Crash Courses to answer";

  const [activeTab, setActiveTab] = useState<string>(mapInitialTab(initialTab));

  const {
    messages,
    lessons,
    fetchLessonsToDo,

    lessonsByScore,
    isLoadingLessons,
    isLoadingLessonsByScore,
    isLoadingMessageByTag,
    errorMessageByTag,
    messageByTag,
    fetchMessageByTag,
    errorLessons,
    errorLessonsByScore,
    fetchLessons,
    fetchLessonsByScore,
    setTargetMessageId,
    reactedMessages,
    isLoadingReactions,
    errorReactions,
    fetchReactions,
    favoriteMessages,
    isLoadingFavoriteMessages,
    errorFavoriteMessages,
    fetchFavoriteMessages,

    fetchTagMessages,
    tagMessages,
    clearInfoMessages,
    fetchCrashCourses,
    crashCourses,
  } = useChatStore();
  const { setDomainFocus, markedIds, focus, domain, isAssessmentOpen } =
    useAppStore();
  const [activeReaction, setActiveReaction] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());

  useEffect(() => {
    clearInfoMessages();
  }, [focus, clearInfoMessages]);

  useEffect(() => {
    if (isOpen && !isClosing) {
      const mappedTab = mapInitialTab(initialTab);
      setActiveTab(isCrashCoursesToAnswer ? "Favorite" : mappedTab);
      setContentSnapshot({
        isCrashCoursesToAnswer,
        activeTab: isCrashCoursesToAnswer ? "Favorite" : mappedTab,
      });
    }
  }, [initialTab, isOpen, isCrashCoursesToAnswer, isClosing]);

  useEffect(() => {
    if (isOpen && activeTab === "Lessons") {
      fetchLessons();
    }
  }, [isOpen, activeTab, fetchLessons]);

  useEffect(() => {
    if (
      isOpen &&
      (activeTab === "Favorite" || activeTab === "Reactions" || activeTab === "My Tags")
    ) {
      fetchLessons();
      fetchLessonsToDo();
      fetchCrashCourses();
    }
  }, [isOpen, activeTab, fetchLessons, fetchLessonsToDo, fetchCrashCourses]);

  useEffect(() => {
    if (isOpen && activeTab === "Crash Courses") {
      fetchCrashCourses();
    }
  }, [isOpen, activeTab, fetchCrashCourses]);

  useEffect(() => {
    if (isOpen && isCrashCoursesToAnswer) {
      fetchCrashCourses();
    }
  }, [isOpen, isCrashCoursesToAnswer, fetchCrashCourses]);

  useEffect(() => {
    if (isOpen && activeTab === "Reactions") {
      if (!activeReaction && Object.keys(markedIds).length > 0) {
        setActiveReaction(Object.keys(markedIds)[0]);
      } else if (activeReaction) {
        fetchReactions(activeReaction);
      }
    }
  }, [isOpen, activeTab, activeReaction, fetchReactions, markedIds]);

  useEffect(() => {
    if (isOpen && activeTab === "Favorite") {
      if (!isCrashCoursesToAnswer) {
        fetchFavoriteMessages();
      }
    }
  }, [isOpen, activeTab, fetchFavoriteMessages, isCrashCoursesToAnswer]);

  useEffect(() => {
    if (isOpen && activeTab === "My Tags") {
      fetchTagMessages();
    }
  }, [isOpen, activeTab, fetchTagMessages]);

  useEffect(() => {
    if (isOpen && activeTab === "My Tags") {
      clearInfoMessages();
    }
  }, [isOpen, activeTab, fetchTagMessages, clearInfoMessages]);

  const handleLessonClick = async (
    lessonId: string,
    localDomain: string,
    localFocus: string,
  ) => {
    try {
      if (isAssessmentOpen) return;
      if (focus !== localFocus || domain !== localDomain) {
        await setDomainFocus(localDomain, localFocus);
      }
      setTargetMessageId(lessonId);
      handleClose();
      setTimeout(() => onMessageSelect?.(), 400);
    } catch (err) {
      console.error("Error in handleLessonClick:", err);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const handleClose = () => {
    setContentSnapshot({
      isCrashCoursesToAnswer,
      activeTab,
    });
    setIsClosing(true);
    onClose();
  };

  const renderContent = () => {
    const effectiveIsCrashCoursesToAnswer = isClosing
      ? contentSnapshot.isCrashCoursesToAnswer
      : isCrashCoursesToAnswer;
    const effectiveActiveTab = isClosing
      ? contentSnapshot.activeTab
      : activeTab;

    if (effectiveIsCrashCoursesToAnswer) {
      return <CrashCoursesPanel onMessageClick={handleLessonClick} />;
    }

    switch (effectiveActiveTab) {
      case "Favorite":
        return (
          <FavoriteMessagesPanel
            favoriteMessages={favoriteMessages}
            isLoading={isLoadingFavoriteMessages}
            error={errorFavoriteMessages}
            onMessageClick={handleLessonClick}
            lessons={lessons}
            messages={messages}
            crashCourses={crashCourses}
          />
        );
      case "My Tags":
        return (
          <TagMessagePanel
            tags={tagMessages}
            onMessageClick={handleLessonClick}
            messageByTag={messageByTag}
            isLoadingMessageByTag={isLoadingMessageByTag}
            errorMessageByTag={errorMessageByTag}
            fetchMessageByTag={fetchMessageByTag}
            lessons={lessons}
            messages={messages}
            crashCourses={crashCourses}
          />
        );
      case "Lessons":
        return (
          <LessonsPanel
            initialViewMode={initialLessonsViewMode}
            lessons={lessons}
            lessonsByScore={lessonsByScore}
            isLoading={isLoadingLessons}
            isLoadingLessonsByScore={isLoadingLessonsByScore}
            error={errorLessons}
            errorLessonsByScore={errorLessonsByScore}
            onLessonClick={handleLessonClick}
            fetchLessonsByScore={fetchLessonsByScore}
          />
        );
      case "Crash Courses":
        return <CrashCoursesPanel onMessageClick={handleLessonClick} />;
      case "Reactions":
        return (
          <ReactionsPanel
            markedIds={markedIds}
            activeReaction={activeReaction}
            setActiveReaction={setActiveReaction}
            reactedMessages={reactedMessages}
            isLoading={isLoadingReactions}
            error={errorReactions}
            onMessageClick={handleLessonClick}
            lessons={lessons}
            messages={messages}
            crashCourses={crashCourses}
          />
        );
      case "Calendar":
        return (
          <CalendarPanel
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            onClose={handleClose}
          />
        );
      case "Search":
        return <SearchMessagesPanel onMessageClick={handleLessonClick} />;
      default:
        return (
          <CustomTypography
            fontWeight={600}
            fontSize={"20px"}
            color="text.secondary"
          >
            No data available for {effectiveActiveTab}
          </CustomTypography>
        );
    }
  };

  const popoverAnchorProps = anchorEl
    ? {
        anchorEl,
        anchorReference: "anchorEl" as const,
      }
    : {
        anchorReference: "anchorPosition" as const,
        anchorPosition: {
          top: 80,
          left: typeof window !== "undefined" ? window.innerWidth - 320 : 0,
        },
      };

  return (
    <Popover
      open={isOpen}
      onClose={handleClose}
      {...popoverAnchorProps}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      PaperProps={{
        sx: popoverPaperStyles,
        overflow: 1,
      }}
      TransitionProps={{
        onExited: () => {
          setIsClosing(false);
        },
      }}
    >
      <Box sx={headerBoxStyles}>
        <CustomTypography fontWeight={600} fontSize={"20px"}>
          {(
            isClosing
              ? contentSnapshot.isCrashCoursesToAnswer
              : isCrashCoursesToAnswer
          )
            ? "Crash Courses"
            : "Interaction Management"}
        </CustomTypography>
        <IconButton onClick={handleClose}>
          <CancelIcon />
        </IconButton>
      </Box>

      {!(isClosing
        ? contentSnapshot.isCrashCoursesToAnswer
        : isCrashCoursesToAnswer) && (
        <Tabs
          value={isClosing ? contentSnapshot.activeTab : activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile={true}
          sx={tabsStyles}
        >
          {tabs.map((tab) => (
            <Tab key={tab} label={tab} value={tab} />
          ))}
        </Tabs>
      )}

      <Box sx={contentBoxStyles}>
        {!(isClosing
          ? contentSnapshot.isCrashCoursesToAnswer
          : isCrashCoursesToAnswer) && (
          <CustomTypography fontWeight={600} fontSize={"20px"} sx={{ mb: 2 }}>
            {isClosing ? contentSnapshot.activeTab : activeTab}{" "}
            {(isClosing ? contentSnapshot.activeTab : activeTab) !==
              "Calendar" && "List"}
          </CustomTypography>
        )}
        {renderContent()}
      </Box>
    </Popover>
  );
};
